<template>

  <div>
    <!-- HERO SECTION -->
    <section class="relative h-[85vh] min-h-[520px] w-full overflow-hidden pt-20">
      <!-- Background Image -->
      <img src="/clinic-interior/hero-22.webp" alt="Modern Dental Clinic Interior"
        width="1920" height="1440" fetchpriority="high" loading="eager" decoding="async"
        class="absolute inset-0 w-full h-full object-cover" />

      <!-- Dark Overlay -->
      <div class="absolute inset-0 bg-black/45"></div>

      <!-- Content -->
      <div class="relative z-10 h-full flex items-center">
        <div class="max-w-6xl mx-auto px-6">
          <h1 class="text-white text-3xl sm:text-4xl md:text-[2.75rem]
              font-extrabold leading-tight max-w-4xl">
            Trusted Dental Clinic in Guwahati
            <span class="block text-[#f5c29c]">
              for Complete Dental Care
            </span>
          </h1>

          <p class="mt-6 text-gray-200 text-base sm:text-lg max-w-xl">
            Experience expert-led dental care with state-of-the-art equipment,
            personalized treatment plans, and a clinic designed for patient comfort.
          </p>

          <div class="mt-8 flex flex-wrap gap-4">
            <!-- Book Appointment -->
            <router-link to="/book-appointment-dental-clinic-guwahati" class="bg-[#924f19] hover:bg-[#7b4215]
                text-white px-6 py-3 rounded-full
                font-semibold transition inline-flex items-center">
              Book Appointment
            </router-link>

            <!-- View Treatments -->
            <router-link to="/dental-services" class="bg-white/90 hover:bg-white
                text-[#924f19] px-6 py-3 rounded-full
                font-semibold transition inline-flex items-center">
              View Treatments
            </router-link>
          </div>
        </div>
      </div>
    </section>


    <!-- INTRO / CAROUSEL SECTION -->
    <section class="bg-[#fef9f6] py-16">
      <div class="max-w-7xl mx-auto px-6">
        <h2 class="text-center text-2xl md:text-3xl font-bold text-[#924f19] mb-4">
          Advanced Care. Modern Technology. Confident Smiles.
        </h2>

        <p class="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          MAGNUM DENTAL CLINIC-Exceptional standards of dental care, made affordable in Guwahati.
        </p>

        <ImageCarousel />
      </div>
    </section>


    <!-- Meet Our Doctors -->
    <section class="bg-gray-50 py-20">

      <!-- Heading -->
      <h2 class="text-center mb-20 px-4 sm:px-0">
        <span class="block text-3xl md:text-4xl font-bold text-[#924f19]">
          Meet the Dental Experts at Magnum Dental Clinic
        </span>
        <span class="block mt-3 text-lg text-gray-600">
          Compassionate, experienced, and dedicated to your dental health.
        </span>
      </h2>


      <!-- Full-bleed visual area -->
      <div class="w-full mb-24">

        <!-- Unified Image + Message Card -->
        <div class="max-w-[1400px] mx-auto bg-white rounded-3xl shadow-xl
             grid md:grid-cols-2 overflow-hidden">

          <!-- Image -->
          <div class="h-full">
            <img src="/doctors-together.webp" alt="Our dental specialists"
              width="1400" height="1034" loading="lazy" decoding="async" class="w-full h-full object-cover" />
          </div>

          <!-- Message -->
          <div class="p-10 md:p-14 lg:p-16 flex items-center">
            <p class="font-serif text-gray-700 text-lg md:text-xl leading-relaxed italic">
              Magnum Dental Clinic, Guwahati is led by Dr. Monami Bujarbaruah and
              Dr. Dimpy Das, who have gained extensive experience working closely
              with patients across various reputed hospitals and clinics. Magnum
              Dental was established with the belief that dentistry should be humane,
              ethical, and transparent, ensuring every patient feels respected and
              well-informed. With strict sterilisation and hygiene protocols and a
              deep commitment to quality, Magnum Dental strives to provide care that
              feels safe, comfortable, and genuinely supportive- because every smile
              deserves thoughtful attention.
            </p>
          </div>

        </div>
      </div>

      <!-- Individual Doctors -->
      <div class="max-w-7xl mx-auto px-6">
        <div class="grid md:grid-cols-2 gap-14">

          <!-- Doctor 1 -->
          <div class="bg-white rounded-2xl shadow-md p-8 md:p-10
               hover:shadow-lg transition">
            <div class="text-center mb-8">
              <img src="/drMonamiNew.webp" alt="Dr. Monami Bujarbaruah"
                width="400" height="534" loading="lazy" decoding="async"
                class="w-32 h-32 mx-auto rounded-full object-cover mb-5" style="object-position: center 30%" />

              <h3 class="text-xl font-semibold text-gray-800 mb-1">
                Dr. Monami Bujarbaruah
              </h3>

              <p class="text-sm text-teal-700 font-medium">
                BDS, MDS (Peridontist and Implantologist)
              </p>
            </div>

            <p class="text-gray-600 leading-relaxed text-center md:text-left">
              Dr. Monami Bujarbaruah is a Periodontist and Implantologist who focuses
              on restoring and preserving natural teeth, helping patients maintain
              long-term oral health with comfort and confidence. With experience in
              dental implants, she offers carefully planned tooth-replacement
              solutions designed for stability and function. Her clinical expertise
              also includes advanced gum treatments, surgical extractions, teeth
              whitening, and cosmetic dental procedures, among others. Dr. Monami is
              known for her gentle approach, clear communication, and personalised
              care, ensuring that every treatment is tailored to each patient’s
              needs and comfort.
            </p>
          </div>

          <!-- Doctor 2 -->
          <div class="bg-white rounded-2xl shadow-md p-8 md:p-10
               hover:shadow-lg transition">
            <div class="text-center mb-4">
              <img src="/drDimpy.webp" alt="Dr. Dimpy Das"
                width="400" height="567" loading="lazy" decoding="async"
                class="w-32 h-32 mx-auto rounded-full object-cover mb-5" style="object-position: center 30%" />

              <h3 class="text-xl font-semibold text-gray-800 mb-1">
                Dr. Dimpy Das
              </h3>

              <p class="text-sm text-teal-700 font-medium">
                BDS (Experienced in Painless Root Canal Treatments and Cosmetic Dental Procedures)
              </p>
            </div>

            <p class="text-gray-600 leading-relaxed text-center md:text-left">
              Dr. Dimpy Das has extensive experience in painless root canal
              treatment, focusing on accuracy, comfort, and preserving natural
              teeth. She is known for her calm and reassuring manner, helping
              patients feel relaxed and confident throughout their treatment.
              Dr. Dimpy is skilled in managing pediatric patients with care and
              patience, ensuring a comfortable and positive dental experience.
              Along with RCTs, her clinical expertise includes restorative dental
              procedures, extractions, preventive care, and aesthetic dental
              treatments, delivered with precision and attention to detail.
              Dr. Dimpy is valued for her methodical approach to treatment. She
              remains focused on delivering precise care with consistent clinical
              outcomes.
            </p>
          </div>

        </div>
      </div>

    </section>


    <!-- Testimonials Carousel -->
    <section class="bg-white py-20 px-4" data-aos="fade-up">
      <div class="max-w-6xl mx-auto text-center">
        <h2 class="text-center text-3xl md:text-4xl font-bold text-[#924f19] mb-20">
          What Our Patients Say
        </h2>

        <Swiper :modules="modules" :space-between="30" :loop="true"
          :autoplay="{ delay: 2000, disableOnInteraction: false }"
          :pagination="{ el: '.swiper-pagination', clickable: true }" :breakpoints="{
            640: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
          }">
          <SwiperSlide v-for="(testimonial, index) in testimonials" :key="index">
            <div class="bg-gray-100 p-6 rounded-xl shadow h-full mx-4">
              <p class="text-gray-700 italic mb-4">“{{ testimonial.text }}”</p>
              <div class="text-teal-700 font-semibold">
                — {{ testimonial.author }}
              </div>
            </div>
          </SwiperSlide>
        </Swiper>

        <!-- Pagination Dots -->
        <div class="swiper-pagination mt-6 flex justify-center"></div>
      </div>
    </section>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import { Autoplay, Pagination } from "swiper/modules";
import ImageCarousel from "@/components/ImageCarousel.vue";
import { useHead } from "@vueuse/head";


export default {
  name: "HomeView",
  components: {
    Swiper,
    SwiperSlide,
    ImageCarousel,
  },
  setup() {
    useHead({
      title: "Magnum Dental Clinic Guwahati | Trusted Dental Care",
      meta: [
        {
          name: "description",
          content:
            "Magnum Dental Clinic is a trusted dental clinic in Guwahati offering expert dental care — implants, root canal, teeth cleaning, braces, and more. Book an appointment today.",
        },
        {
          name: "robots",
          content: "index, follow",
        },
        {
          property: "og:title",
          content: "Magnum Dental Clinic Guwahati | Trusted Dental Care",
        },
        {
          property: "og:description",
          content:
            "Expert dental care in Guwahati. Dental implants, root canal treatment, braces, teeth whitening, and more at Magnum Dental Clinic.",
        },
        {
          property: "og:url",
          content: "https://www.magnumdentalguwahati.in/",
        },
      ],
      link: [
        { rel: "canonical", href: "https://www.magnumdentalguwahati.in/" },
        {
          rel: "preload",
          as: "image",
          href: "/clinic-interior/hero-22.webp",
          fetchpriority: "high",
        },
      ],
    });
  },
  data() {
    return {
      modules: [Autoplay, Pagination],
      testimonials: [
        {
          text: "Excellent experience from start to finish. The doctors explained every step clearly and made the entire treatment completely painless. The clinic feels professional, friendly, and very well organized.",
          author: "Dipanbita Kalita",
        },
        {
          text: "Wonderful care by the doctors and staff. My crown treatment was done with great precision and the fitting was perfect with no discomfort at all. Truly professional service that I would highly recommend.",
          author: "Gitartha Goswami",
        },
        {
          text: "I was initially nervous, but the doctors explained everything so well that I felt completely at ease. The treatment was gentle and painless, and now I feel much more aware of my dental health.",
          author: "Kamakhya Das",
        },
        {
          text: "Clean, hygienic clinic with a very calm and welcoming atmosphere. Even as a new mother, I felt comfortable throughout my visit. The staff is warm, patient, and genuinely caring.",
          author: "Priyanka Pandey",
        },
      ],
      isMobileMenuOpen: false,
      methods: {
        closeMobileMenu() {
          this.isMobileMenuOpen = false;
        },
      },
    };
  },
};
</script>
